import { ref } from "vue";
import { PermissionType } from "~~/types/user";

/**
 * Composable for playing notification sounds
 * Generates a pleasant notification beep using Web Audio API
 */
export const useNotificationSound = () => {
  let audioContext: AudioContext | null = null;
  let repeatIntervalId: ReturnType<typeof setInterval> | null = null;
  const isRepeating = ref(false);

  /**
   * Initialize audio context (lazy initialization)
   */
  const getAudioContext = (): AudioContext | null => {
    if (!audioContext && typeof window !== "undefined") {
      try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.warn("Web Audio API not supported:", error);
        return null;
      }
    }
    return audioContext;
  };

  /**
   * Play a notification sound
   * Generates a pleasant two-tone beep using Web Audio API
   */
  const playNotificationSound = async () => {
    // Check if user has permission to disable case request notification sound
    const userStore = useUserStore();
    if (userStore.currentUser?.permissions.includes(PermissionType.DISABLE_CASE_REQUEST_NOTIFICATION_SOUND)) {
      return;
    }

    try {
      const context = getAudioContext();
      if (!context) return;

      // Resume context if suspended (required for Chrome's autoplay policy)
      if (context.state === "suspended") {
        await context.resume();
      }

      const now = context.currentTime;

      // Create oscillator for first tone (higher pitch)
      const oscillator1 = context.createOscillator();
      const gainNode1 = context.createGain();

      oscillator1.connect(gainNode1);
      gainNode1.connect(context.destination);

      // First tone: 800 Hz
      oscillator1.frequency.setValueAtTime(800, now);
      oscillator1.type = "sine";

      // Fade in and out for smoother sound
      gainNode1.gain.setValueAtTime(0, now);
      gainNode1.gain.linearRampToValueAtTime(0.3, now + 0.01);
      gainNode1.gain.linearRampToValueAtTime(0, now + 0.15);

      oscillator1.start(now);
      oscillator1.stop(now + 0.15);

      // Create oscillator for second tone (lower pitch)
      const oscillator2 = context.createOscillator();
      const gainNode2 = context.createGain();

      oscillator2.connect(gainNode2);
      gainNode2.connect(context.destination);

      // Second tone: 600 Hz, plays after first tone
      oscillator2.frequency.setValueAtTime(600, now + 0.15);
      oscillator2.type = "sine";

      // Fade in and out
      gainNode2.gain.setValueAtTime(0, now + 0.15);
      gainNode2.gain.linearRampToValueAtTime(0.3, now + 0.16);
      gainNode2.gain.linearRampToValueAtTime(0, now + 0.3);

      oscillator2.start(now + 0.15);
      oscillator2.stop(now + 0.3);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  };

  /**
   * Start repeating the notification sound every 1.5 seconds
   */
  const startRepeatingSound = () => {
    // Don't start if already repeating
    if (isRepeating.value) {
      return;
    }

    // Play sound immediately
    playNotificationSound();

    // Start repeating every 3 seconds
    repeatIntervalId = setInterval(() => {
      playNotificationSound();
    }, 1500);

    isRepeating.value = true;
  };

  /**
   * Stop repeating the notification sound
   */
  const stopRepeatingSound = () => {
    if (repeatIntervalId) {
      clearInterval(repeatIntervalId);
      repeatIntervalId = null;
    }
    isRepeating.value = false;
  };

  return {
    playNotificationSound,
    startRepeatingSound,
    stopRepeatingSound,
    isRepeating,
  };
};
